import React, { useState } from 'react';
import { View, TextInput, TouchableOpacity } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { translate } from '../../../services/translate';
import FilterCheckboxList, { FilterListItem } from './FilterCheckboxList';
import MyAppText from '../../MyAppText';

interface Props {
    search: string;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
    handcuffUsed: boolean;
    setHandcuffUsed: React.Dispatch<React.SetStateAction<boolean>>;
    arrested: boolean;
    setArrested: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function InvolvedPersonList({
    search,
    setSearch,
    handcuffUsed,
    setHandcuffUsed,
    arrested,
    setArrested,
}: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const [inputValue, setInputValue] = useState('');

    const addTag = () => {
        const trimmed = inputValue.trim();
        if (trimmed) {
            setSearch(trimmed);
        }
        setInputValue('');
    };

    const removeTag = () => {
        setSearch('');
    };

    const options: FilterListItem[] = [
        { label: translate('handcuffUsed'), value: 1 },
        { label: translate('arrested'), value: 2 },
    ];

    const selected: number[] = [
        ...(handcuffUsed ? [1] : []),
        ...(arrested ? [2] : []),
    ];

    const handleChange: React.Dispatch<React.SetStateAction<number[]>> = (action) => {
        const newValue = typeof action === 'function' ? action(selected) : action;
        setHandcuffUsed(newValue.includes(1));
        setArrested(newValue.includes(2));
    };

    return (
        <View style={styles.container}>
            <View style={styles.searchWrapper}>
                <MyAppText style={styles.label}>{translate('searchByNameOrCpf')}</MyAppText>

                <TextInput
                    style={[styles.input, !!search && styles.inputDisabled]}
                    placeholder={translate('typeToSearch')}
                    placeholderTextColor={theme.colors.neutral['300']}
                    value={inputValue}
                    onChangeText={setInputValue}
                    onSubmitEditing={addTag}
                    blurOnSubmit={false}
                    returnKeyType='done'
                    editable={!search}
                />

                {!!search && (
                    <View style={styles.tagsContainer}>
                        <View style={styles.tag}>
                            <MyAppText style={styles.tagText}>{search}</MyAppText>
                            <TouchableOpacity onPress={removeTag}>
                                <FontAwesomeIcon icon={faXmark as IconDefinition} fontSize={12} color={theme.colors.labelColor} />
                            </TouchableOpacity>
                        </View>
                    </View>
                )}
            </View>


            <FilterCheckboxList
                items={options}
                value={selected}
                setValue={handleChange}
            />
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 16,
    },
    searchWrapper: {
        gap: 8,
    },
    label: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    input: {
        fontSize: 16,
        color: theme.colors.neutral['400'],
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        outlineStyle: 'none',
    },
    tagsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    tag: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.neutral['200'],
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
    },
    tagText: {
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    inputDisabled: {
        backgroundColor: theme.colors.neutral['200'],
        opacity: 0.6,
    },
}));
